import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { HeroInput } from './hero-input';
import { ProgressIndicator } from './progress-indicator';
import { TranscriptDisplay } from './transcript-display';
import { MetadataPanel } from './metadata-panel';
import { ExportToolbar } from './export-toolbar';
import { apiFetch, API_BASE } from '../../lib/api';
import { trackTranscriptionStarted } from '../../lib/analytics';
import { PROCESSING_SPEED_FACTOR, DEFAULT_ESTIMATED_SEC } from '../../lib/transcriptionProgress';
import InsufficientBalanceModal from '../InsufficientBalanceModal';
import { VerificationRequiredModal } from '../VerificationRequiredModal';
import { useAuth } from '../../contexts/AuthContext';
import { useTranscriptionJobs } from '../../contexts/TranscriptionContext';
import { finalizeTranscriptionAndNavigate } from './navigate-after-transcription';
import { SpeakerAvatar } from "@/components/SpeakerAvatar";
import { Thumbnail } from "@/components/Thumbnail";
import { displayTitle } from '@/lib/displayTitle';

type TranscriptionState = 'idle' | 'processing' | 'complete' | 'error';

interface Speaker {
  speaker: string;
  text: string;
  timestamp?: string;
  start_ms?: number;
  end_ms?: number;
}

interface ApiSpeaker {
  id: string;
  name?: string;
  display_name?: string;
  role?: string;
  bio?: string;
  description?: string;
  confidence: number;
  image_url?: string;
}

interface ApiSegment {
  speaker: string;
  text: string;
  start_ms: number;
  end_ms: number;
}

// --- Batch types ---

type ItemStatus = 'queued' | 'pending' | 'downloading' | 'transcribing' | 'completed' | 'cached' | 'failed';

interface BatchItem {
  id?: string;
  url: string;
  status: ItemStatus;
  progress: number;
  message: string;
  title?: string;
  channel_name?: string;
  summary?: string;
  result_item_id?: string;
  error?: string | null;
  transcript_json?: ApiSegment[];
  speakers_json?: ApiSpeaker[];
  duration_ms?: number;
  stage?: string;
}

interface Folder {
  id: string;
  name: string;
  item_count: number;
}

interface UrlPreview {
  title: string;
  author: string;
  duration_minutes?: number;
}

interface Recommendation {
  video_id: string;
  title: string;
  channel_name?: string;
  summary?: string;
  thumbnail_url?: string;
  url: string;
}

// --- Helpers ---

function msToTimestamp(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function msToDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function ensureSegmentsArray(raw: unknown): ApiSegment[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.segments)) return obj.segments;
    return Object.values(obj).filter(
      (v: unknown) => v && typeof v === 'object' && 'text' in (v as Record<string, unknown>),
    ) as ApiSegment[];
  }
  return [];
}

function extractVideoId(url: string): string | null {
  const match = url.match(/[?&]v=([^&]+)/);
  return match ? match[1] : null;
}

function decodeEntities(text: string): string {
  const el = document.createElement('textarea');
  el.innerHTML = text;
  return el.value;
}

const STATUS_COLORS: Record<ItemStatus, string> = {
  queued: 'bg-gray-200 text-gray-700',
  pending: 'bg-gray-200 text-gray-700',
  downloading: 'bg-yellow-400 text-white',
  transcribing: 'bg-blue-500 text-white',
  completed: 'bg-green-500 text-white',
  cached: 'bg-green-500 text-white',
  failed: 'bg-red-500 text-white',
};

const STATUS_LABELS: Record<ItemStatus, string> = {
  queued: 'Queued',
  pending: 'Pending',
  downloading: 'Downloading',
  transcribing: 'Transcribing',
  completed: 'Completed',
  cached: 'Completed',
  failed: 'Failed',
};

const SPEAKER_COLORS = [
  { bg: 'bg-blue-50', badge: 'bg-blue-100 text-blue-700' },
  { bg: 'bg-green-50', badge: 'bg-green-100 text-green-700' },
  { bg: 'bg-purple-50', badge: 'bg-purple-100 text-purple-700' },
  { bg: 'bg-orange-50', badge: 'bg-orange-100 text-orange-700' },
  { bg: 'bg-pink-50', badge: 'bg-pink-100 text-pink-700' },
  { bg: 'bg-teal-50', badge: 'bg-teal-100 text-teal-700' },
];

const FRIENDLY_ERRORS: Record<string, string> = {
  video_unavailable:
    'YouTube prevents automated downloads of some videos. Try the podcast\'s RSS feed instead, or upload the audio file.',
  service_busy:
    'Our YouTube service is temporarily busy. Try again shortly or use an RSS feed for instant results.',
  bot_gate:
    'YouTube is currently blocking automated downloads of this video. Please try again later or try a different video.',
};

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const obj = err as Record<string, unknown>;
    if (typeof obj.error_type === 'string' && FRIENDLY_ERRORS[obj.error_type]) {
      return FRIENDLY_ERRORS[obj.error_type];
    }
    if (typeof obj.message === 'string') return obj.message;
  }
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return fallback;
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function getTimeAwareMessage(elapsed: number): string {
  if (elapsed < 30) return 'Starting transcription...';
  if (elapsed < 120) return 'Downloading audio...';
  if (elapsed < 300) return 'Transcribing audio — this may take a few minutes for longer videos...';
  if (elapsed < 600) return 'Still working — longer videos take more time. Feel free to open a new tab!';
  if (elapsed < 900) return 'Almost there — processing a long video...';
  return 'This is a long one! Still processing — you can leave this tab open and come back.';
}

// Normalize /batch/upload/{id} response shape (data.jobs) into BatchItem[] for the poll consumer.
// /batch/{id} returns data.items; /batch/upload/{id} returns data.jobs with different keys.
function normalizeUploadBatchResponse(data: any): BatchItem[] {
  if (!Array.isArray(data.jobs)) return [];
  return data.jobs.map((job: any): BatchItem => ({
    id: job.job_id,
    url: job.job_id || '',
    status: job.status as ItemStatus,
    progress: job.progress ?? 0,
    message: job.message || '',
    error: job.error ?? null,
    result_item_id: job.result?.item_id,
    title: job.result?.title,
  }));
}

// --- Component ---

export function TranscriptionContainer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshBalance } = useAuth();
  const { jobs: contextJobs, addJob, addBatchJob, updateJob, addCompletedJob } = useTranscriptionJobs();
  const cacheHitBalanceNotifiedRef = useRef<Set<string>>(new Set());

  // Common state
  const [state, setState] = useState<TranscriptionState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [balanceError, setBalanceError] = useState(false);
  const [balanceMinutesRemaining, setBalanceMinutesRemaining] = useState(0);
  const [balanceMinutesNeeded, setBalanceMinutesNeeded] = useState(0);
  const [pendingTranscription, setPendingTranscription] = useState<{url: string; folderId?: string | null; language?: string} | null>(null);
  const [minutesUsed, setMinutesUsed] = useState<number | null>(null);

  // Single transcription state
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [transcript, setTranscript] = useState<Speaker[]>([]);
  const [currentUrl, setCurrentUrl] = useState('');
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [metadata, setMetadata] = useState({
    duration: '00:00',
    wordCount: 0,
    speakerCount: 0,
  });
  const [resultTitle, setResultTitle] = useState('');
  const [resultChannelName, setResultChannelName] = useState('');
  const [resultSummary, setResultSummary] = useState('');
  const [resultSpeakers, setResultSpeakers] = useState<ApiSpeaker[]>([]);
  const [wasTruncated, setWasTruncated] = useState(false);

  // Guest upsell modal state (shown when content exceeds remaining minutes)
  const [guestUpsell, setGuestUpsell] = useState<{
    show: boolean;
    contentMinutes: number;
    remainingMinutes: number;
    urls: string[];
    folderId?: string | null;
    language?: string;
  } | null>(null);

  // Badge editing state (speaker rename via badges)
  const [editingBadgeName, setEditingBadgeName] = useState<string | null>(null);
  const [editingBadgeValue, setEditingBadgeValue] = useState('');

  // Batch state
  const [batchId, setBatchId] = useState<string | null>(null);
  const [batchKind, setBatchKind] = useState<'url' | 'upload' | null>(null);
  const [batchItems, setBatchItems] = useState<BatchItem[]>([]);
  const batchPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Batch expand state
  const [expandedBatchItems, setExpandedBatchItems] = useState<Set<number>>(new Set());
  const [batchRecs, setBatchRecs] = useState<Record<number, Recommendation[]>>({});
  const [batchCopied, setBatchCopied] = useState<Record<number, boolean>>({});

  // Save-to-folder state (post-transcription, for batch save-all)
  const [folders, setFolders] = useState<Folder[]>([]);
  const [folderOpen, setFolderOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // oEmbed preview state
  const [previews, setPreviews] = useState<Record<string, UrlPreview>>({});

  // Balance state
  const [balance, setBalance] = useState<number | null>(null);
  
  // Verification required modal state
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [emailVerified, setEmailVerified] = useState(true);
  const [verificationLimits, setVerificationLimits] = useState<{
    limit: number;
    bonus: number;
  } | null>(null);

  // Upload progress state (0-100, null = not uploading)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  // Elapsed time tracking for long transcriptions
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const processingStartRef = useRef<number | null>(null);

  // Poll retry tracking
  const pollRetryCountRef = useRef(0);
  const retryPollRef = useRef<(() => void) | null>(null);
  const MAX_POLL_RETRIES = 3;
  const POLL_RETRY_DELAY = 3000;

  // Fetch balance on mount / when user changes (works for guests too via IP)
  useEffect(() => {
    apiFetch<{ 
      minutes_remaining: number; 
      email_verified?: boolean;
      verification_required?: boolean;
    }>('/billing/balance')
      .then((data) => {
        setBalance(data.minutes_remaining);
        // Track verification status
        if (data.email_verified !== undefined) {
          setEmailVerified(data.email_verified);
        }
        // If verification required (at limit + unverified), prepare modal data
        if (data.verification_required) {
          setVerificationLimits({
            limit: 60, // 1 hour unverified limit
            bonus: 1140 // 19 more hours when verified
          });
        }
      })
      .catch(() => setBalance(null));
  }, [user]);

  // --- Processing timer & notifications ---

  const startProcessingTimer = useCallback(() => {
    document.title = 'Transcribing... | Echonour';
    setElapsedSeconds(0);
    processingStartRef.current = Date.now();
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    elapsedTimerRef.current = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - (processingStartRef.current || Date.now())) / 1000));
    }, 1000);
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const stopProcessingTimer = useCallback(() => {
    document.title = 'Echonour';
    setElapsedSeconds(0);
    processingStartRef.current = null;
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  }, []);

  const notifyComplete = useCallback((title?: string) => {
    document.title = 'Complete! | Echonour';
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
    if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
      new Notification('Transcription complete!', {
        body: title ? `Your transcript for "${title}" is ready.` : 'Your transcript is ready.',
      });
    }
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
      document.title = 'Echonour';
    };
  }, []);

  // Reset timer when state returns to idle (handles all error paths)
  useEffect(() => {
    if (state === 'idle' && processingStartRef.current !== null) {
      stopProcessingTimer();
    }
  }, [state, stopProcessingTimer]);

  // Computed
  const allBatchDone =
    batchItems.length > 0 &&
    batchItems.every((j) => j.status === 'completed' || j.status === 'cached' || j.status === 'failed');
  const isBatchProcessing = batchId !== null;

  // --- Batch polling ---

  const stopBatchPolling = useCallback(() => {
    if (batchPollRef.current) {
      clearInterval(batchPollRef.current);
      batchPollRef.current = null;
    }
  }, []);

  useEffect(() => stopBatchPolling, [stopBatchPolling]);

  // Auto-fetch recommendations for all completed batch items when batch finishes
  useEffect(() => {
    if (!allBatchDone) return;
    batchItems.forEach((item, i) => {
      if (item.status === 'completed' || item.status === 'cached') {
        fetchBatchItemRecs(i, item.url);
      }
    });
  }, [allBatchDone]);

  // Notify when batch completes
  useEffect(() => {
    if (allBatchDone && isBatchProcessing) {
      notifyComplete('your batch transcription');
    }
  }, [allBatchDone]);

  useEffect(() => {
    if (!batchId) return;
    let batchRetryCount = 0;

    const startBatchPolling = () => {
      const poll = async () => {
        try {
          const pollUrl = batchKind === 'upload' ? `/batch/upload/${batchId}` : `/batch/${batchId}`;
          const data = await apiFetch<any>(pollUrl);
          batchRetryCount = 0;
          const itemList: BatchItem[] = batchKind === 'upload'
            ? normalizeUploadBatchResponse(data)
            : Array.isArray(data.items) ? data.items : [];
          setBatchItems(itemList);
          
          // Sync each item's status to global context for bottom bar
          itemList.forEach((item: BatchItem) => {
            const statusMap: Record<string, 'polling' | 'completed' | 'failed'> = {
              queued: 'polling',
              processing: 'polling',
              completed: 'completed',
              cached: 'completed',
              failed: 'failed',
            };
            const progressMap: Record<string, number> = {
              queued: 10,
              processing: 50,
              completed: 100,
              cached: 100,
              failed: 100,
            };
            const stageMap: Record<string, string> = {
              queued: 'Queued',
              processing: item.stage || 'Transcribing...',
              completed: 'Completed',
              cached: 'Completed (cached)',
              failed: 'Failed',
            };
            updateJob(item.id, {
              status: statusMap[item.status] || 'polling',
              progress: item.progress != null ? Math.round(item.progress * 100) : (progressMap[item.status] || 0),
              stage: stageMap[item.status] || item.status,
              resultItemId: item.result_item_id,
              error: item.error,
            });
          });
          
          const done = itemList.every(
            (j) => j.status === 'completed' || j.status === 'cached' || j.status === 'failed',
          );
          if (done) stopBatchPolling();
        } catch {
          stopBatchPolling();
          batchRetryCount += 1;
          if (batchRetryCount <= MAX_POLL_RETRIES) {
            setStatus(`Connection lost — retrying (${batchRetryCount}/${MAX_POLL_RETRIES})...`);
            setTimeout(startBatchPolling, POLL_RETRY_DELAY);
          } else {
            retryPollRef.current = () => {
              batchRetryCount = 0;
              setErrorMessage('');
              setState('processing');
              setStatus('Reconnecting...');
              startBatchPolling();
            };
            setErrorMessage('Connection lost');
            setState('error');
          }
        }
      };

      poll();
      batchPollRef.current = setInterval(poll, 2000);
    };

    startBatchPolling();
    return stopBatchPolling;
  }, [batchId, batchKind, stopBatchPolling, updateJob]);

  // --- Auto-start from URL param or location state ---

  useEffect(() => {
    // Check URL param first
    const urlParam = searchParams.get('url');
    if (urlParam && state === 'idle') {
      setSearchParams({}, { replace: true });
      setCurrentUrl(urlParam);
      startSingleTranscription(urlParam);
      return;
    }
    
    // Check location state (from Browse page transcribe button)
    const locationState = location.state as { transcribeUrl?: string; episodeTitle?: string } | null;
    if (locationState?.transcribeUrl && state === 'idle') {
      // Clear the state to prevent re-triggering on navigation
      navigate('/', { replace: true, state: {} });
      setCurrentUrl(locationState.transcribeUrl);
      startSingleTranscription(locationState.transcribeUrl);
    }
  }, []);

  // Reset state when navigating back to "/" (e.g. clicking Echonour logo)
  // Watch location.key so even same-path navigations (/ → /) trigger a reset
  useEffect(() => {
    if (location.pathname === '/' && state !== 'idle') {
      stopBatchPolling();
      stopProcessingTimer();
      setState('idle');
      setProgress(0);
      setStatus('');
      setTranscript([]);
      setErrorMessage('');
      setBalanceError(false);
      setCurrentUrl('');
      setBatchId(null);
      setBatchKind(null);
      setBatchItems([]);
      setFolderOpen(false);
      setExpandedBatchItems(new Set());
      setBatchRecs({});
      setBatchCopied({});
      setPreviews({});
      setRecommendations([]);
      setMinutesUsed(null);
      setMetadata({ duration: '00:00', wordCount: 0, speakerCount: 0 });
      setResultTitle('');
      setResultChannelName('');
      setResultSummary('');
      setResultSpeakers([]);
      setUploadProgress(null);
      setEditingBadgeName(null);
      retryPollRef.current = null;
    }
  }, [location.key]);

  // --- oEmbed preview ---

  const handleUrlBlur = (url: string) => {
    if (previews[url]) return;

    // Fetch duration from backend (works for any URL)
    fetch(`${API_BASE}/video-info?url=${encodeURIComponent(url)}`)
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data && data.duration_sec) {
          setPreviews((prev) => ({
            ...prev,
            [url]: { ...prev[url], title: prev[url]?.title || '', author: prev[url]?.author || '', duration_minutes: Math.ceil(data.duration_sec / 60) },
          }));
        }
      })
      .catch(() => {});

    // YouTube: also fetch oEmbed for title/author
    if (!url.includes('youtube.com/') && !url.includes('youtu.be/')) return;

    fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`)
      .then((res) => {
        if (!res.ok) throw new Error('oEmbed failed');
        return res.json();
      })
      .then((data) => {
        if (data.title) {
          setPreviews((prev) => ({
            ...prev,
            [url]: { ...prev[url], title: data.title, author: data.author_name || '' },
          }));
        }
      })
      .catch(() => {});
  };

  // --- Recommendations ---

  const fetchRecommendations = (url: string) => {
    const videoId = extractVideoId(url);
    if (!videoId) return;
    const token = localStorage.getItem('tempo_token');
    fetch(`${API_BASE}/recommendations?video_id=${encodeURIComponent(videoId)}`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then((res) => res.json())
      .then((data) => setRecommendations(data.recommendations ?? []))
      .catch(() => {});
  };

  // --- Single transcription ---

  const startSingleTranscription = async (url: string, folderId?: string | null, language?: string) => {
    setState('processing');
    startProcessingTimer();
    setProgress(0);
    setStatus('Submitting job...');
    setErrorMessage('');
    setBalanceError(false);
    setMinutesUsed(null);
    setWasTruncated(false);
    setRecommendations([]);
    setResultTitle('');
    setResultChannelName('');
    setResultSummary('');
    setResultSpeakers([]);

    try {
      const token = localStorage.getItem('tempo_token');
      // For guests, include session ID so transcription can be migrated when they sign up
      const guestSessionId = !token ? (await import('@/lib/api')).getGuestSessionId() : undefined;
      const submitRes = await fetch(`${API_BASE}/transcribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ 
          url, 
          folder_id: folderId || undefined, 
          language: language || 'auto',
          guest_session_id: guestSessionId,
        }),
      });

      if (submitRes.status === 401) {
        localStorage.removeItem('tempo_token');
        setErrorMessage('Session expired. Please try again.');
        setState('idle');
        return;
      }

      if (submitRes.status === 402) {
        const body = await submitRes.json().catch(() => ({}));
        const detail = body.detail || body;
        
        // Check if this is a verification-required error
        if (detail.verification_required || (detail.minutes_remaining <= 0 && !emailVerified)) {
          setVerificationLimits({
            limit: 60,
            bonus: 1140
          });
          setShowVerificationModal(true);
          setState('idle');
          return;
        }
        
        const minutesRemaining = detail.minutes_remaining ?? body.minutes_remaining ?? 0;
        const previewMinutes = previews[url]?.duration_minutes ?? 0;
        const estimatedMinutesNeeded = Math.max(0, Math.ceil(previewMinutes - minutesRemaining));
        setBalanceMinutesRemaining(minutesRemaining);
        setBalanceMinutesNeeded(detail.minutes_needed ?? body.minutes_needed ?? estimatedMinutesNeeded);
        setPendingTranscription({ url, folderId, language });
        setBalanceError(true);
        setState('idle');
        return;
      }

      if (!submitRes.ok) {
        const body = await submitRes.json().catch(() => ({}));
        // Check for specific error_type with friendly messages
        if (typeof body.error_type === 'string' && FRIENDLY_ERRORS[body.error_type]) {
          throw new Error(FRIENDLY_ERRORS[body.error_type]);
        }
        throw new Error(
          typeof body.detail === 'string' ? body.detail
          : typeof body.message === 'string' ? body.message
          : typeof body.error === 'string' ? body.error
          : 'Failed to submit transcription job'
        );
      }

      const submitData = await submitRes.json();
      
      // Track transcription started
      trackTranscriptionStarted('url');

      // Cache hit — result already available, auto-redirect to library
      if (submitData.status === 'completed' && submitData.result) {
        const itemId = submitData.result.saved_item_id || submitData.result.item_id || submitData.result.id || submitData.item_id;
        
        stopProcessingTimer();
        setState('idle');
        
        // Auto-redirect to library for full audio player experience
        if (itemId) {
          const cacheJobId = submitData.job_id || itemId;
          if (!cacheHitBalanceNotifiedRef.current.has(cacheJobId)) {
            cacheHitBalanceNotifiedRef.current.add(cacheJobId);
            refreshBalance();
          }
          finalizeTranscriptionAndNavigate(navigate, { kind: 'item', itemId });
          return;
        }
        
        // Fallback: add to bottom bar if no item ID (shouldn't happen)
        const title = submitData.result.title || previews[url]?.title || 'Transcription';
        const cacheJobId = `cache_${Date.now()}`;
        addCompletedJob(cacheJobId, title, undefined);
        return;
      }

      const jobId = submitData.job_id;

      // Register with global progress bar — stays on current page, bottom bar shows progress
      const durationMin = previews[currentUrl]?.duration_minutes ?? 0;
      const estimatedSec = durationMin > 0
        ? Math.round((durationMin * 60) / PROCESSING_SPEED_FACTOR)
        : DEFAULT_ESTIMATED_SEC;
      addJob(jobId, previews[currentUrl]?.title || currentUrl, estimatedSec);
      
      stopProcessingTimer();
      // navigate unmounts this component, so setState('idle') would be a no-op.
      finalizeTranscriptionAndNavigate(navigate, { kind: 'job', jobId });
      return;

      // Note: The code below is kept for reference but will not execute
      // as we now navigate to the TranscriptionPage which handles polling
      
      // Poll for progress with retry logic
      pollRetryCountRef.current = 0;

      const startPolling = () => {
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await fetch(`${API_BASE}/job/${jobId}`, {
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
            });

            if (statusRes.status === 401) {
              clearInterval(pollInterval);
              localStorage.removeItem('tempo_token');
              setErrorMessage('Session expired. Please try again.');
              setState('idle');
              return;
            }

            const statusData = await statusRes.json();

            // Successful poll — reset retry counter
            pollRetryCountRef.current = 0;
            setProgress(Math.round(statusData.progress * 100));
            setStatus(statusData.message || 'Processing...');

            if (statusData.status === 'completed' && statusData.result) {
              clearInterval(pollInterval);

              const speakerNames: Record<string, string> = {};
              (statusData.result.speakers || []).forEach((s: ApiSpeaker) => {
                const name = s.display_name || s.name;
                if (name) speakerNames[s.id] = name;
              });

              const segments = ensureSegmentsArray(statusData.result.segments);
              const displayTranscript: Speaker[] = segments.map((seg) => ({
                speaker: speakerNames[seg.speaker] || seg.speaker,
                text: seg.text,
                timestamp: msToTimestamp(seg.start_ms),
                start_ms: seg.start_ms,
                end_ms: seg.end_ms,
              }));

              setTranscript(displayTranscript);

              const wordCount = displayTranscript.reduce(
                (acc, item) => acc + item.text.split(' ').length,
                0,
              );
              const speakerCount = new Set(displayTranscript.map((item) => item.speaker)).size;
              const lastSeg = segments[segments.length - 1];
              const durationMs = statusData.result.duration_ms || lastSeg?.end_ms || 0;

              setMetadata({
                duration: durationMs ? msToDuration(durationMs) : '-',
                wordCount,
                speakerCount,
              });

              setResultTitle(statusData.result.title || '');
              setResultChannelName(statusData.result.channel_name || '');
              setResultSummary(statusData.result.summary || '');
              setResultSpeakers(statusData.result.speakers || []);
              setMinutesUsed(statusData.result?.minutes_used ?? statusData.minutes_used ?? null);
              setWasTruncated(statusData.result?.was_truncated || false);
              notifyComplete(statusData.result.title || '');
              setState('complete');
              fetchRecommendations(url);
            }

            if (statusData.status === 'failed') {
              clearInterval(pollInterval);
              setErrorMessage(extractErrorMessage(statusData.error, 'Transcription failed'));
              setState('idle');
            }
          } catch {
            clearInterval(pollInterval);
            pollRetryCountRef.current += 1;
            if (pollRetryCountRef.current <= MAX_POLL_RETRIES) {
              setStatus(`Connection lost — retrying (${pollRetryCountRef.current}/${MAX_POLL_RETRIES})...`);
              setTimeout(startPolling, POLL_RETRY_DELAY);
            } else {
              retryPollRef.current = () => {
                pollRetryCountRef.current = 0;
                setErrorMessage('');
                setState('processing');
                setStatus('Reconnecting...');
                startPolling();
              };
              setErrorMessage('Connection lost');
              setState('error');
            }
          }
        }, 2000);
      };

      startPolling();
    } catch (err: unknown) {
      setErrorMessage(extractErrorMessage(err, 'Failed to connect to API'));
      setState('idle');
    }
  };

  // Proceed anyway (bypass balance check, guest 45min limit applies)
  const handleProceedAnyway = () => {
    if (!pendingTranscription) return;
    const { url, folderId, language } = pendingTranscription;
    setPendingTranscription(null);
    setBalanceError(false);
    setBalanceMinutesNeeded(0);
    // Retry the transcription - backend will truncate at 45min for guests
    startSingleTranscription(url, folderId, language);
  };

  // --- Batch transcription ---

  const startBatchTranscription = async (urls: string[], folderId?: string | null, language?: string) => {
    setState('processing');
    startProcessingTimer();
    setErrorMessage('');
    setBalanceError(false);
    setMinutesUsed(null);

    try {
      const data = await apiFetch<any>('/batch', {
        method: 'POST',
        body: JSON.stringify({ urls, folder_id: folderId || undefined, language: language || 'auto' }),
      });
      if (Array.isArray(data.items)) {
        setBatchItems(data.items);
        // Add each batch item to global context for bottom bar tracking
        data.items.forEach((item: BatchItem) => {
          addBatchJob(item.id, item.title || item.url || 'Transcription');
        });
      }
      setBatchId(data.batch_id);
      setBatchKind('url');
    } catch (err) {
      setErrorMessage(extractErrorMessage(err, 'Failed to submit batch'));
      setState('idle');
    }
  };

  // --- File upload transcription ---

  const startFileTranscription = (files: File[], folderId?: string | null, language?: string) => {
    setState('processing');
    startProcessingTimer();
    setProgress(0);
    setStatus('Uploading...');
    setErrorMessage('');
    setBalanceError(false);
    setMinutesUsed(null);
    setRecommendations([]);
    setResultTitle('');
    setResultChannelName('');
    setResultSummary('');
    setResultSpeakers([]);
    setUploadProgress(0);

    const token = localStorage.getItem('tempo_token');
    if (!token) {
      setErrorMessage('Please sign in to upload files');
      setState('idle');
      setUploadProgress(null);
      return;
    }

    const isBatch = files.length > 1;
    const formData = new FormData();
    if (isBatch) {
      files.forEach((file) => formData.append('files', file));
    } else {
      formData.append('file', files[0]);
    }
    
    // Add folder_id to form data if specified
    if (folderId) {
      formData.append('folder_id', folderId);
    }
    
    // Add language to form data
    formData.append('language', language || 'auto');

    const xhr = new XMLHttpRequest();
    const endpoint = isBatch ? `${API_BASE}/batch/upload` : `${API_BASE}/transcribe/upload`;

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        setUploadProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      setUploadProgress(null);

      if (xhr.status === 401) {
        localStorage.removeItem('tempo_token');
        setErrorMessage('Session expired. Please try again.');
        setState('idle');
        return;
      }

      if (xhr.status === 402) {
        try {
          const body = JSON.parse(xhr.responseText);
          const detail = body.detail || body;
          
          // Check if this is a verification-required error
          if (detail.verification_required || (detail.minutes_remaining <= 0 && !emailVerified)) {
            setVerificationLimits({
              limit: 60,
              bonus: 1140
            });
            setShowVerificationModal(true);
            setState('idle');
            return;
          }
          
          setBalanceMinutesRemaining(detail.minutes_remaining ?? body.minutes_remaining ?? 0);
          setBalanceMinutesNeeded(detail.minutes_needed ?? body.minutes_needed ?? 0);
        } catch {
          setBalanceMinutesRemaining(0);
          setBalanceMinutesNeeded(0);
        }
        setBalanceError(true);
        setState('idle');
        return;
      }

      let data: any;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        setErrorMessage('Invalid response from server');
        setState('idle');
        return;
      }

      if (xhr.status >= 400) {
        setErrorMessage(
          typeof data.detail === 'string' ? data.detail
          : typeof data.message === 'string' ? data.message
          : typeof data.error === 'string' ? data.error
          : 'Upload failed'
        );
        setState('idle');
        return;
      }

      // Track upload started
      trackTranscriptionStarted(isBatch ? 'batch' : 'upload');

      if (isBatch) {
        // Batch upload response — start polling like URL batch
        if (Array.isArray(data.items)) {
          setBatchItems(data.items);
        }
        setBatchId(data.batch_id);
        setBatchKind('upload');
        setStatus('Processing...');
      } else {
        // Single upload response — poll for job progress
        const jobId = data.job_id;
        if (!jobId) {
          // Possible immediate result (cache hit)
          if (data.status === 'completed' && data.result) {
            setProgress(100);
            handleUploadResult(data.result, data);
            return;
          }
          setErrorMessage('No job ID returned from upload');
          setState('idle');
          return;
        }

        setStatus('Processing...');
        pollRetryCountRef.current = 0;

        const startUploadPolling = () => {
          const pollInterval = setInterval(async () => {
            try {
              const statusRes = await fetch(`${API_BASE}/upload/job/${jobId}`, {
                headers: { Authorization: `Bearer ${token}` },
              });

              if (statusRes.status === 401) {
                clearInterval(pollInterval);
                localStorage.removeItem('tempo_token');
                setErrorMessage('Session expired. Please try again.');
                setState('idle');
                return;
              }

              const statusData = await statusRes.json();

              pollRetryCountRef.current = 0;
              setProgress(Math.round(statusData.progress * 100));
              setStatus(statusData.message || 'Processing...');

              if (statusData.status === 'completed' && statusData.result) {
                setProgress(100);
                clearInterval(pollInterval);
                // If recovered from restart, redirect to library item
                if (statusData.result.recovered && statusData.result.item_id) {
                  window.location.href = `/transcript/${statusData.result.item_id}`;
                  return;
                }
                handleUploadResult(statusData.result, statusData);
              }

              if (statusData.status === 'failed') {
                clearInterval(pollInterval);
                setErrorMessage(extractErrorMessage(statusData.error, 'Transcription failed'));
                setState('idle');
              }
            } catch {
              clearInterval(pollInterval);
              pollRetryCountRef.current += 1;
              if (pollRetryCountRef.current <= MAX_POLL_RETRIES) {
                setStatus(`Connection lost — retrying (${pollRetryCountRef.current}/${MAX_POLL_RETRIES})...`);
                setTimeout(startUploadPolling, POLL_RETRY_DELAY);
              } else {
                retryPollRef.current = () => {
                  pollRetryCountRef.current = 0;
                  setErrorMessage('');
                  setState('processing');
                  setStatus('Reconnecting...');
                  startUploadPolling();
                };
                setErrorMessage('Connection lost');
                setState('error');
              }
            }
          }, 2000);
        };

        startUploadPolling();
      }
    };

    xhr.onerror = () => {
      setUploadProgress(null);
      setErrorMessage('Upload failed — check your connection');
      setState('idle');
    };

    xhr.open('POST', endpoint);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  };

  // Shared helper to parse a completed upload result
  const handleUploadResult = (result: any, wrapper: any) => {
    const speakerNames: Record<string, string> = {};
    (result.speakers || []).forEach((s: ApiSpeaker) => {
      const name = s.display_name || s.name;
      if (name) speakerNames[s.id] = name;
    });
    const segments = ensureSegmentsArray(result.segments);
    const displayTranscript: Speaker[] = segments.map((seg) => ({
      speaker: speakerNames[seg.speaker] || seg.speaker,
      text: seg.text,
      timestamp: msToTimestamp(seg.start_ms),
      start_ms: seg.start_ms,
      end_ms: seg.end_ms,
    }));
    setTranscript(displayTranscript);
    const wordCount = displayTranscript.reduce(
      (acc, item) => acc + item.text.split(' ').length,
      0,
    );
    const speakerCount = new Set(displayTranscript.map((item) => item.speaker)).size;
    const lastSeg = segments[segments.length - 1];
    const durationMs = result.duration_ms || lastSeg?.end_ms || 0;
    setMetadata({
      duration: durationMs ? msToDuration(durationMs) : '-',
      wordCount,
      speakerCount,
    });
    setResultTitle(result.title || '');
    setResultChannelName(result.channel_name || '');
    setResultSummary(result.summary || '');
    setResultSpeakers(result.speakers || []);
    setMinutesUsed(result?.minutes_used ?? wrapper.minutes_used ?? null);
    notifyComplete(result.title || '');
    // Private-upload completion: redirect to library item, mirroring URL-flow
    // line ~671 and the never-merged fix/upload-redirect branch (line 417).
    const itemId =
      result.saved_item_id ||
      result.item_id ||
      result.id ||
      wrapper?.item_id;
    if (itemId) {
      const uploadJobId = wrapper?.job_id || itemId;
      if (!cacheHitBalanceNotifiedRef.current.has(uploadJobId)) {
        cacheHitBalanceNotifiedRef.current.add(uploadJobId);
        refreshBalance();
      }
      setProgress(100);
      finalizeTranscriptionAndNavigate(navigate, { kind: 'item', itemId });
      return;
    }
    setState('complete');
  };

  // --- Inline editing handlers ---

  const handleRenameSpeaker = (oldName: string, newName: string) => {
    // Look up the original speaker ID (e.g. "SPEAKER_1") from resultSpeakers
    // since transcript segments store display names, not raw IDs
    const speakerInfo = resultSpeakers.find(
      (s) => (s.display_name || s.name) === oldName,
    );
    const speakerId = speakerInfo?.id || oldName;

    setTranscript((prev) =>
      prev.map((seg) => (seg.speaker === oldName ? { ...seg, speaker: newName } : seg)),
    );
    setResultSpeakers((prev) =>
      prev.map((s) => {
        const displayName = s.display_name || s.name;
        if (displayName === oldName) return { ...s, display_name: newName, name: newName };
        return s;
      }),
    );
    setMetadata((prev) => ({
      ...prev,
      speakerCount: new Set(
        transcript.map((seg) => (seg.speaker === oldName ? newName : seg.speaker)),
      ).size,
    }));
    const videoId = extractVideoId(currentUrl);
    console.log('[RenameSpeaker] speakerId:', speakerId, 'oldName:', oldName, '→', newName);
    apiFetch('/edit/rename-speaker', {
      method: 'POST',
      body: JSON.stringify({ old_name: speakerId, new_name: newName, video_id: videoId }),
    }).catch(() => console.warn('Failed to persist speaker rename'));
  };

  const startBadgeEdit = (name: string) => {
    setEditingBadgeName(name);
    setEditingBadgeValue(name);
  };

  const commitBadgeEdit = () => {
    if (!editingBadgeName) return;
    const oldName = editingBadgeName;
    const newName = editingBadgeValue.trim();
    if (!newName || newName === oldName) {
      setEditingBadgeName(null);
      return;
    }
    handleRenameSpeaker(oldName, newName);
    setEditingBadgeName(null);
  };

  const cancelBadgeEdit = () => {
    setEditingBadgeName(null);
  };

  const handleEditText = (index: number, newText: string) => {
    setTranscript((prev) =>
      prev.map((seg, i) => (i === index ? { ...seg, text: newText } : seg)),
    );
    setMetadata((prev) => {
      const newWordCount = transcript.reduce(
        (acc, seg, i) => acc + (i === index ? newText : seg.text).split(' ').length,
        0,
      );
      return { ...prev, wordCount: newWordCount };
    });
    // Fire and forget — persist the full updated transcript
    const updatedTranscript = transcript.map((seg, i) =>
      i === index ? { ...seg, text: newText } : seg,
    );
    const videoId = extractVideoId(currentUrl);
    apiFetch('/edit/transcript', {
      method: 'PATCH',
      body: JSON.stringify({ transcript_json: updatedTranscript, video_id: videoId }),
    }).catch(() => console.warn('Failed to persist text edit'));
  };

  // --- Handlers ---

  const handleSubmit = (urls: string[], folderId?: string | null, language?: string) => {
    // For guests, show upsell modal only if content exceeds remaining minutes
    if (!user && urls.length === 1) {
      const preview = previews[urls[0]];
      const contentMinutes = preview?.duration_minutes || 0;
      const guestRemaining = balance ?? 45; // Default 45 min for guests
      
      if (contentMinutes > guestRemaining) {
        setGuestUpsell({
          show: true,
          contentMinutes,
          remainingMinutes: guestRemaining,
          urls,
          folderId,
          language,
        });
        return;
      }
      // Content fits within limit - proceed without modal
    }
    
    proceedWithTranscription(urls, folderId, language);
  };
  
  const proceedWithTranscription = (urls: string[], folderId?: string | null, language?: string) => {
    if (urls.length === 1) {
      setCurrentUrl(urls[0]);
      startSingleTranscription(urls[0], folderId, language);
    } else {
      startBatchTranscription(urls, folderId, language);
    }
  };

  const handleExport = (format: 'txt' | 'srt' | 'vtt' | 'json') => {
    let content = '';
    const filename = `transcript.${format}`;

    switch (format) {
      case 'txt':
        content = transcript.map((item) => `${item.speaker}: ${item.text}`).join('\n\n');
        break;

      case 'srt':
        content = transcript
          .map((item, index) => {
            const startTime = item.timestamp || '00:00:00';
            const endTime = transcript[index + 1]?.timestamp || '00:00:00';
            return `${index + 1}\n${startTime},000 --> ${endTime},000\n${item.speaker}: ${item.text}\n`;
          })
          .join('\n');
        break;

      case 'vtt':
        content =
          'WEBVTT\n\n' +
          transcript
            .map((item, index) => {
              const startTime = item.timestamp || '00:00:00';
              const endTime = transcript[index + 1]?.timestamp || '00:00:00';
              return `${startTime}.000 --> ${endTime}.000\n${item.speaker}: ${item.text}`;
            })
            .join('\n\n');
        break;

      case 'json':
        content = JSON.stringify({ transcript, metadata }, null, 2);
        break;
    }

    const blob = new Blob([content], { type: 'text/plain' });
    const dlUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = dlUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(dlUrl);
  };

  const handleStartNew = () => {
    stopBatchPolling();
    stopProcessingTimer();
    setState('idle');
    setProgress(0);
    setStatus('');
    setTranscript([]);
    setErrorMessage('');
    setBalanceError(false);
    setCurrentUrl('');
    setBatchId(null);
    setBatchKind(null);
    setBatchItems([]);
    setFolderOpen(false);
    setExpandedBatchItems(new Set());
    setBatchRecs({});
    setBatchCopied({});
    setPreviews({});
    setRecommendations([]);
    setMinutesUsed(null);
    setMetadata({ duration: '00:00', wordCount: 0, speakerCount: 0 });
    setResultTitle('');
    setResultChannelName('');
    setResultSummary('');
    setResultSpeakers([]);
    setUploadProgress(null);
    setEditingBadgeName(null);
  };

  // --- Batch item expand/export ---

  const toggleBatchItem = (index: number) => {
    setExpandedBatchItems((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
        fetchBatchItemRecs(index, batchItems[index]?.url);
      }
      return next;
    });
  };

  function batchItemMetadata(item: BatchItem) {
    const t = parseBatchTranscript(item);
    const wordCount = t.reduce((acc, s) => acc + s.text.split(' ').length, 0);
    const speakerCount = new Set(t.map((s) => s.speaker)).size;
    const segments = ensureSegmentsArray(item.transcript_json ?? []);
    const lastSeg = segments[segments.length - 1];
    const durationMs = item.duration_ms || lastSeg?.end_ms || 0;
    return {
      duration: durationMs ? msToDuration(durationMs) : '-',
      wordCount,
      speakerCount,
    };
  }

  function fetchBatchItemRecs(index: number, url: string) {
    if (batchRecs[index]) return;
    const videoId = extractVideoId(url);
    if (!videoId) return;
    const token = localStorage.getItem('tempo_token');
    fetch(`${API_BASE}/recommendations?video_id=${encodeURIComponent(videoId)}`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const recs = data.recommendations ?? [];
        if (recs.length > 0) {
          setBatchRecs((prev) => ({ ...prev, [index]: recs }));
        }
      })
      .catch(() => {});
  }

  function parseBatchTranscript(item: BatchItem): Speaker[] {
    if (!item.transcript_json) return [];
    const segments = ensureSegmentsArray(item.transcript_json);
    const speakerNames: Record<string, string> = {};
    (item.speakers_json || []).forEach((s) => {
      const name = s.display_name || s.name;
      if (name) speakerNames[s.id] = name;
    });
    return segments.map((seg) => ({
      speaker: speakerNames[seg.speaker] || seg.speaker,
      text: seg.text,
      timestamp: msToTimestamp(seg.start_ms),
      start_ms: seg.start_ms,
      end_ms: seg.end_ms,
    }));
  }

  function buildBatchMarkdown(item: BatchItem): string {
    const t = parseBatchTranscript(item);
    const displayTitle = item.title || 'Transcript';
    const speakers = [...new Set(t.map((s) => s.speaker))];
    let md = `# ${displayTitle}\n\n## Speakers\n`;
    for (const name of speakers) md += `- ${name}\n`;
    md += `\n## Transcript\n\n`;
    for (const seg of t) {
      const ts = seg.timestamp ? ` (${seg.timestamp})` : '';
      md += `**${seg.speaker}**${ts}\n${seg.text}\n\n`;
    }
    return md.trimEnd() + '\n';
  }

  function handleBatchCopy(index: number, item: BatchItem) {
    const md = buildBatchMarkdown(item);
    navigator.clipboard.writeText(md).then(() => {
      setBatchCopied((prev) => ({ ...prev, [index]: true }));
      setTimeout(() => setBatchCopied((prev) => ({ ...prev, [index]: false })), 2000);
    });
  }

  function handleBatchAiExport(item: BatchItem) {
    const md = buildBatchMarkdown(item);
    const displayTitle = item.title || 'Transcript';
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${displayTitle.replace(/[^a-z0-9]/gi, '_').substring(0, 50)}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const handleBatchItemExport = (item: BatchItem, format: 'txt' | 'srt' | 'vtt' | 'json') => {
    const itemTranscript = parseBatchTranscript(item);
    if (itemTranscript.length === 0) return;

    let content = '';
    const safeTitle = (item.title || 'transcript').replace(/[^a-z0-9]/gi, '_').substring(0, 50);
    const filename = `${safeTitle}.${format}`;

    switch (format) {
      case 'txt':
        content = itemTranscript.map((s) => `${s.speaker}: ${s.text}`).join('\n\n');
        break;
      case 'srt':
        content = itemTranscript
          .map((s, idx) => {
            const start = s.timestamp || '00:00:00';
            const end = itemTranscript[idx + 1]?.timestamp || '00:00:00';
            return `${idx + 1}\n${start},000 --> ${end},000\n${s.speaker}: ${s.text}\n`;
          })
          .join('\n');
        break;
      case 'vtt':
        content =
          'WEBVTT\n\n' +
          itemTranscript
            .map((s, idx) => {
              const start = s.timestamp || '00:00:00';
              const end = itemTranscript[idx + 1]?.timestamp || '00:00:00';
              return `${start}.000 --> ${end}.000\n${s.speaker}: ${s.text}`;
            })
            .join('\n\n');
        break;
      case 'json':
        content = JSON.stringify({ transcript: itemTranscript }, null, 2);
        break;
    }

    const blob = new Blob([content], { type: 'text/plain' });
    const dlUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = dlUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(dlUrl);
  };

  // --- Save-to-folder ---

  async function openFolderDropdown() {
    if (!folderOpen) {
      try {
        const data = await apiFetch<Folder[] | { folders: Folder[] }>('/folders');
        const list = Array.isArray(data) ? data : (data as any).folders || [];
        setFolders(list);
      } catch {
        // ignore
      }
    }
    setFolderOpen(!folderOpen);
  }

  async function saveAllToFolder(folderId: string) {
    if (!batchId) return;
    setSaving(true);
    try {
      await apiFetch(`/batch/${batchId}/save-all`, {
        method: 'POST',
        body: JSON.stringify({ folder_id: folderId }),
      });
      setFolderOpen(false);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  }

  // --- Render ---

  // Enhanced status with time-aware messages for long transcriptions
  const enhancedStatus = (() => {
    if (state !== 'processing' || uploadProgress !== null || elapsedSeconds === 0) return status;
    const msg = getTimeAwareMessage(elapsedSeconds);
    const elapsed = formatElapsed(elapsedSeconds);
    return `${msg} (${elapsed} elapsed)`;
  })();

  const isUnverifiedUser = !!user && (user.email_verified === false || emailVerified === false);

  return (
    <div className="relative min-h-screen">
        {/* Guest upsell modal - shown when content exceeds remaining minutes */}
        {guestUpsell?.show && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Content exceeds free limit</h3>
              <p className="mt-2 text-sm text-gray-600">
                This content is <span className="font-medium">{guestUpsell.contentMinutes} minutes</span>. 
                You have <span className="font-medium">{guestUpsell.remainingMinutes} minutes</span> remaining as a guest — the rest will be truncated.
              </p>
              <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 p-3">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Sign up free</span> to get 20 hours of transcription and transcribe the full content.
                </p>
              </div>
              <div className="mt-6 flex flex-col gap-3">
                <a
                  href="/signup"
                  className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-center text-sm font-medium text-white hover:bg-blue-700"
                >
                  Sign up for full content
                </a>
                <button
                  onClick={() => {
                    setGuestUpsell(null);
                    proceedWithTranscription(
                      guestUpsell.urls,
                      guestUpsell.folderId,
                      guestUpsell.language
                    );
                  }}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Continue with {guestUpsell.remainingMinutes} minutes
                </button>
                <button
                  onClick={() => setGuestUpsell(null)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {(state === 'idle' && (!user || isUnverifiedUser)) && (
          <div className="w-full max-w-4xl mx-auto px-4 mb-4">
            <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800">
              {!user ? (
                <>
                  <span className="font-medium">Free tier:</span> You have 45 minutes of transcription — longer content gets cut off.{' '}
                  <a href="/signup" className="underline font-medium hover:text-blue-900">Sign up</a> for full access and an extra 60 minutes on the house. Confirm your email and we&apos;ll throw in another 5 hours 🍄
                </>
              ) : (
                <>
                  <span className="font-medium">Don&apos;t forget:</span> Confirm your email to get +5h.
                </>
              )}
            </div>
          </div>
        )}

        <HeroInput
          onSubmit={handleSubmit}
          collapsed={state !== 'idle'}
          onUrlBlur={handleUrlBlur}
          previews={previews}
          balance={balance}
          inlineError={state === 'idle' && !balanceError ? errorMessage : undefined}
          onPurchaseMore={() => {
            setBalanceMinutesNeeded(0);
            setBalanceError(true);
          }}
          onFilesSelected={startFileTranscription}
          isLoggedIn={!!user}
        />

      {/* Transcription Progress — shows context-tracked jobs (e.g. from RSS episodes) */}
      {state === 'idle' && !isBatchProcessing && (() => {
        const activeContextJobs = contextJobs.filter((j) => !j.hidden);
        if (activeContextJobs.length === 0) return null;
        return (
          <div className="w-full max-w-4xl mx-auto py-8 space-y-6">
            <div className="flex items-center justify-between">
              <h2
                className="text-lg font-semibold"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                Transcription Progress
              </h2>
            </div>

            <div className="space-y-3">
              {activeContextJobs.map((job) => {
                const isComplete = job.status === 'completed';
                const isFailed = job.status === 'failed';
                const isPolling = job.status === 'polling';

                return (
                  <div key={job.jobId} className="rounded-lg border bg-white overflow-hidden">
                    <div className="px-4 py-3 space-y-2">
                      <div className="flex items-center justify-between gap-4">
                        <p className="min-w-0 flex-1 truncate text-sm text-gray-800">
                          {displayTitle(job.title)}
                        </p>
                        <span
                          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium text-white ${
                            isComplete ? 'bg-green-500' : isFailed ? 'bg-red-500' : 'bg-blue-500'
                          }`}
                        >
                          {isComplete ? 'Completed' : isFailed ? 'Failed' : job.stage || 'Processing'}
                        </span>
                      </div>

                      {isPolling && (
                        <div className="relative h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="absolute top-0 left-0 h-full rounded-full bg-blue-500 transition-all duration-500"
                            style={{ width: `${job.progress}%` }}
                          />
                        </div>
                      )}

                      {isFailed && job.error && (
                        <p className="text-xs text-red-500">{job.error}</p>
                      )}

                      {isComplete && job.resultItemId && (
                        <button
                          onClick={() => navigate(`/transcript/${job.resultItemId}`)}
                          className="text-xs font-medium text-blue-600 hover:text-blue-500"
                        >
                          View transcript
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {activeContextJobs.some((j) => j.status === 'polling') && (
              <div className="flex justify-center gap-2">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-2 w-2 animate-pulse rounded-full bg-blue-500"
                    style={{ animationDelay: `${i * 200}ms` }}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Inline error for non-idle states (idle errors show inside HeroInput) */}
      {state !== 'idle' && errorMessage && (
        <div className="mx-auto max-w-4xl px-4 mt-2 flex items-center gap-3">
          <p className="text-sm text-red-600">{errorMessage}</p>
          {state === 'error' && retryPollRef.current && (
            <button
              onClick={() => retryPollRef.current?.()}
              className="shrink-0 rounded-md bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700"
            >
              Retry
            </button>
          )}
        </div>
      )}

      {/* Upload progress bar */}
      {uploadProgress !== null && state === 'processing' && (
        <div className="mx-auto max-w-4xl px-4 py-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600 shrink-0">Uploading... {uploadProgress}%</span>
            <div className="relative flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="absolute top-0 left-0 h-full rounded-full bg-blue-500 transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Single transcription flow */}
      {!isBatchProcessing && (
        <>
          {state === 'processing' && uploadProgress === null && (
            <ProgressIndicator progress={progress} status={enhancedStatus} />
          )}

          {state === 'complete' && (
            <>
              <MetadataPanel
                duration={metadata.duration}
                wordCount={metadata.wordCount}
                speakerCount={metadata.speakerCount}
              />
              {minutesUsed !== null && minutesUsed > 0 && (
                <p className="mx-auto max-w-4xl text-xs text-gray-400 mt-1 mb-2">
                  {minutesUsed % 1 === 0 ? minutesUsed : minutesUsed.toFixed(1)} minutes used
                </p>
              )}
              
              {/* Truncation warning for guests */}
              {wasTruncated && (
                <div className="mx-auto max-w-4xl mb-4">
                  <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                    <span className="font-medium">⚠️ Partial transcript:</span> This content was truncated to fit your free tier limit. 
                    <a href="/signup" className="underline font-medium hover:text-amber-900 ml-1">Sign up</a> to transcribe full episodes.
                  </div>
                </div>
              )}

              {/* Title, channel, summary, speaker bios */}
              {(resultTitle || resultSummary || resultSpeakers.length > 0) && (
                <div className="mx-auto max-w-4xl space-y-4 mb-4">
                  {resultTitle && (
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{resultTitle}</h2>
                      {resultChannelName && (
                        <p className="text-sm text-gray-500">{resultChannelName}</p>
                      )}
                    </div>
                  )}
                  {resultSummary && (
                    <div className="rounded-lg border border-gray-200 bg-white p-4">
                      <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">Summary</h3>
                      <p className="text-sm leading-relaxed text-gray-700">{resultSummary}</p>
                    </div>
                  )}
                  {resultSpeakers.length > 0 && (() => {
                    const names = resultSpeakers
                      .map((s) => s.display_name || s.name || s.id)
                      .filter(Boolean);
                    const uniqueNames = [...new Set(names)];
                    return uniqueNames.length > 0 ? (
                      <div>
                        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Speakers</h3>
                        <div className="flex flex-wrap gap-3">
                          {uniqueNames.map((name, idx) => {
                            const info = resultSpeakers.find(
                              (s) => (s.display_name || s.name) === name,
                            );
                            const color = SPEAKER_COLORS[idx % SPEAKER_COLORS.length];
                            const bioText = info?.bio || info?.description;
                            return (
                              <div
                                key={name}
                                className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2"
                              >
                                <SpeakerAvatar
                                  name={name}
                                  imageUrl={info?.image_url}
                                  size="md"
                                  className={color.badge}
                                />
                                <div className="min-w-0">
                                  {editingBadgeName === name ? (
                                    <input
                                      type="text"
                                      autoFocus
                                      value={editingBadgeValue}
                                      onChange={(e) => setEditingBadgeValue(e.target.value)}
                                      onBlur={commitBadgeEdit}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') commitBadgeEdit();
                                        if (e.key === 'Escape') cancelBadgeEdit();
                                      }}
                                      className="w-32 rounded border border-blue-300 px-1 py-0.5 text-sm font-medium text-gray-900 outline-none focus:ring-1 focus:ring-blue-400"
                                    />
                                  ) : (
                                    <span
                                      className="cursor-pointer text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline"
                                      onClick={() => startBadgeEdit(name)}
                                      title="Click to edit speaker name"
                                    >
                                      {name}
                                    </span>
                                  )}
                                  {info?.role && info.role !== 'unknown' && (
                                    <p className="text-xs text-gray-500">{info.role}</p>
                                  )}
                                  {bioText && (
                                    <p className="text-xs text-gray-400">{bioText}</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}

              <TranscriptDisplay transcript={transcript} editable onEditText={handleEditText} />
              <ExportToolbar onExport={handleExport} onStartNew={handleStartNew} transcript={transcript} title={previews[currentUrl]?.title} />

              {recommendations.length > 0 && (
                <div className="mx-auto mt-8 max-w-4xl rounded-lg bg-gray-50 p-6">
                  <h2
                    className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500"
                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                  >
                    Recommended
                  </h2>
                  <div className="flex gap-4 overflow-x-auto pb-2">
                    {recommendations.map((rec) => (
                      <button
                        key={rec.video_id}
                        onClick={() => {
                          handleStartNew();
                          setCurrentUrl(rec.url);
                          startSingleTranscription(rec.url);
                        }}
                        className="flex w-[320px] shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white text-left transition-shadow hover:shadow-md"
                      >
                        <Thumbnail
                          src={rec.thumbnail_url}
                          alt={rec.title ?? ""}
                          className="aspect-video w-full object-cover"
                        />
                        <div className="flex flex-1 flex-col gap-1 p-3">
                          <span className="text-sm font-medium text-gray-900 line-clamp-2">{decodeEntities(rec.title)}</span>
                          {rec.channel_name && (
                            <span className="text-xs text-gray-500">{decodeEntities(rec.channel_name)}</span>
                          )}
                          {rec.summary && (
                            <span className="mt-0.5 text-xs text-gray-400">{decodeEntities(rec.summary)}</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Batch transcription flow */}
      {isBatchProcessing && (
        <div className="w-full max-w-4xl mx-auto py-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2
              className="text-lg font-semibold"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Batch Transcription
            </h2>
            <div className="flex items-center gap-2">
              {allBatchDone && user && (
                <div className="relative">
                  <button
                    onClick={openFolderDropdown}
                    disabled={saving}
                    className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save all to folder'}
                  </button>
                  {folderOpen && (
                    <div className="absolute right-0 z-10 mt-1 w-48 rounded-md border bg-white py-1 shadow-lg">
                      {folders.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-gray-400">No folders</p>
                      ) : (
                        folders.map((f) => (
                          <button
                            key={f.id}
                            onClick={() => saveAllToFolder(f.id)}
                            className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                          >
                            {f.name}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
              {allBatchDone && (
                <button
                  onClick={handleStartNew}
                  className="rounded-md border px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  Start new
                </button>
              )}
            </div>
          </div>

          {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}

          <div className="space-y-3">
            {batchItems.map((job, i) => {
              const isComplete = job.status === 'completed' || job.status === 'cached';
              const hasTranscript = isComplete && job.transcript_json && job.transcript_json.length > 0;
              const isExpanded = expandedBatchItems.has(i);

              return (
                <div key={i} className="rounded-lg border bg-white overflow-hidden">
                  <button
                    type="button"
                    onClick={() => hasTranscript && toggleBatchItem(i)}
                    className={`w-full px-4 py-3 space-y-2 text-left ${hasTranscript ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                    disabled={!hasTranscript}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {hasTranscript && (
                          <svg
                            className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                        )}
                        <p className="min-w-0 flex-1 truncate text-sm text-gray-800">
                          {displayTitle(job.title, job.url)}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[job.status]}`}
                      >
                        {STATUS_LABELS[job.status]}
                      </span>
                    </div>

                    {(job.status === 'downloading' || job.status === 'transcribing') && (
                      <div className="relative h-2 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className="absolute top-0 left-0 h-full rounded-full bg-blue-500 transition-all duration-500"
                          style={{ width: `${Math.round(job.progress * 100)}%` }}
                        />
                      </div>
                    )}

                    {(job.message || job.error) && job.status === 'failed' && (
                      <p className="text-xs text-red-500">{job.message || job.error}</p>
                    )}
                  </button>

                  {isExpanded && hasTranscript && (() => {
                    const meta = batchItemMetadata(job);
                    const recs = batchRecs[i] || [];
                    const batchSpeakers = job.speakers_json || [];
                    const batchSpeakerNames = batchSpeakers
                      .map((s) => s.display_name || s.name || s.id)
                      .filter(Boolean);
                    const batchUniqueSpeakers = [...new Set(batchSpeakerNames)];
                    return (
                      <div className="border-t px-4 py-4 space-y-4">
                        <MetadataPanel
                          duration={meta.duration}
                          wordCount={meta.wordCount}
                          speakerCount={meta.speakerCount}
                        />

                        {/* Title, channel, summary, speaker bios */}
                        {(job.title || job.summary || batchUniqueSpeakers.length > 0) && (
                          <div className="space-y-3">
                            {job.title && (
                              <div>
                                <h3 className="text-base font-semibold text-gray-900">{displayTitle(job.title)}</h3>
                                {job.channel_name && (
                                  <p className="text-sm text-gray-500">{job.channel_name}</p>
                                )}
                              </div>
                            )}
                            {job.summary && (
                              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">Summary</h4>
                                <p className="text-sm leading-relaxed text-gray-700">{job.summary}</p>
                              </div>
                            )}
                            {batchUniqueSpeakers.length > 0 && (
                              <div>
                                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Speakers</h4>
                                <div className="flex flex-wrap gap-2">
                                  {batchUniqueSpeakers.map((name, idx) => {
                                    const info = batchSpeakers.find(
                                      (s) => (s.display_name || s.name) === name,
                                    );
                                    const color = SPEAKER_COLORS[idx % SPEAKER_COLORS.length];
                                    return (
                                      <div
                                        key={name}
                                        className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2"
                                      >
                                        <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${color.badge} text-xs font-semibold`}>
                                          {name.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                          <span className="text-sm font-medium text-gray-900">{name}</span>
                                          {info?.role && info.role !== 'unknown' && (
                                            <p className="text-xs text-gray-500">{info.role}</p>
                                          )}
                                          {(info?.bio || info?.description) && (
                                            <p className="text-xs text-gray-400">{info?.bio || info?.description}</p>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        <TranscriptDisplay transcript={parseBatchTranscript(job)} />
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => handleBatchCopy(i, job)}
                            className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                              batchCopied[i]
                                ? 'border-green-500 text-green-600'
                                : 'border-blue-500 text-blue-600 hover:bg-blue-50'
                            }`}
                          >
                            {batchCopied[i] ? 'Copied!' : 'Copy'}
                          </button>
                          <button
                            onClick={() => handleBatchAiExport(job)}
                            className="rounded-md border border-blue-500 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50"
                          >
                            AI Ready
                          </button>
                          {(['txt', 'srt', 'vtt', 'json'] as const).map((fmt) => (
                            <button
                              key={fmt}
                              onClick={() => handleBatchItemExport(job, fmt)}
                              className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 uppercase"
                            >
                              {fmt}
                            </button>
                          ))}
                        </div>
                        {recs.length > 0 && (
                          <div className="rounded-lg bg-gray-50 p-4">
                            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500"
                              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                            >
                              Recommended
                            </h3>
                            <div className="flex gap-3 overflow-x-auto pb-2">
                              {recs.map((rec) => (
                                <button
                                  key={rec.video_id}
                                  onClick={() => {
                                    handleStartNew();
                                    setCurrentUrl(rec.url);
                                    startSingleTranscription(rec.url);
                                  }}
                                  className="flex w-[320px] shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white text-left transition-shadow hover:shadow-md"
                                >
                                  <Thumbnail
                                    src={rec.thumbnail_url}
                                    alt={rec.title ?? ""}
                                    className="h-[180px] w-full object-cover"
                                  />
                                  <div className="flex flex-1 flex-col gap-1 p-2.5">
                                    <span className="text-xs font-medium text-gray-900 line-clamp-2">{decodeEntities(rec.title)}</span>
                                    {rec.channel_name && (
                                      <span className="text-[11px] text-gray-500">{decodeEntities(rec.channel_name)}</span>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>

          {!allBatchDone && (
            <div className="flex justify-center gap-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-2 w-2 animate-pulse rounded-full bg-blue-500"
                  style={{ animationDelay: `${i * 200}ms` }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Insufficient Balance Modal */}
      {balanceError && (
        <InsufficientBalanceModal
          minutesNeeded={balanceMinutesNeeded}
          minutesRemaining={balanceMinutesRemaining}
          onClose={() => {
            setBalanceError(false);
            setBalanceMinutesNeeded(0);
            setPendingTranscription(null);
          }}
          onProceedAnyway={handleProceedAnyway}
          isGuest={!user}
          isUnverifiedUser={!!user && user.email_verified === false}
        />
      )}

      {/* Verification Required Modal */}
      <VerificationRequiredModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        limitType="transcription"
        currentLimit={verificationLimits?.limit || 60}
        upgradeBonus={verificationLimits?.bonus || 1140}
        userEmail={user?.email}
      />
    </div>
  );
}
